/**
 * The "Varundus" tab of the reporter card (item J, docs/saidiseire-varundus.md):
 * the backup sets UpdraftPlus keeps, its last run, every update in the kept
 * reports with the verdict of the backups before it, and the database counts
 * with WP-Optimize's state.
 *
 * A set in UpdraftPlus's history is a finished backup job, not a proven
 * restore; the tab says what was recorded and nothing more.
 */
import type { ReactNode } from "react";
import { cn } from "@wa/ui";
import {
  DB_COUNTS,
  autoloadHeavy,
  backupState,
  backupVerdict,
  setDestinations,
  setHoldsKey,
  updateRows,
  verdictTone,
} from "../../../../lib/monitoringBackup";
import type {
  ReporterBackupView,
  ReporterDatabaseView,
  ReporterExpectView,
  ReporterHistoryRow,
  ReporterReportView,
} from "../../../../lib/monitoringReporter";
import { StatusPill, fmtBytes, fmtDateTime, fmtNum } from "../../../ui";
import type { T } from "../words";
import { cleanupText, scheduleText, storageText } from "./backupWords";

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <p className="mb-2 font-semibold">{title}</p>
      {children}
    </section>
  );
}

const num = (n: number | null) => (n === null ? "-" : fmtNum(n));

function Sets({ b, t }: { b: ReporterBackupView; t: T }) {
  return (
    <Section title={t("monitoring.reporter.backup.setsTitle")}>
      {b.sets.length === 0 ? (
        <p className="crm-mut text-[12.5px]">{t("monitoring.reporter.backup.setsNone")}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="crm-table">
            <thead>
              <tr>
                <th scope="col">{t("monitoring.reporter.backup.col.at")}</th>
                <th scope="col">{t("monitoring.reporter.backup.col.holds")}</th>
                <th scope="col">{t("monitoring.reporter.backup.col.where")}</th>
              </tr>
            </thead>
            <tbody>
              {b.sets.map((s, i) => (
                <tr key={`${s.at}-${i}`}>
                  <td className="crm-tnum">{fmtDateTime(s.at)}</td>
                  <td>{t(setHoldsKey(s))}</td>
                  <td>
                    {/* "kaugel" and "ainult samas serveris" are the words the staff guide uses. */}
                    <span className="inline-flex flex-wrap items-center gap-2">
                      <StatusPill tone={s.remote ? "ok" : "wt"}>
                        {t(s.remote ? "monitoring.reporter.backup.remote" : "monitoring.reporter.backup.localOnly")}
                      </StatusPill>
                      {s.remote ? <span className="crm-mut">{setDestinations(s).map((id) => storageText(t, id)).join(", ")}</span> : null}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="crm-sub mt-2 space-y-0.5 text-[12.5px]">
        {b.setCount > b.sets.length ? <p>{t("monitoring.reporter.backup.setsMore", { n: b.setCount })}</p> : null}
        <p>
          {t("monitoring.reporter.backup.schedule", { db: scheduleText(t, b.dbEvery ?? "manual"), files: scheduleText(t, b.filesEvery ?? "manual") })}
        </p>
        <p>{t("monitoring.reporter.backup.keep", { db: num(b.keepDb), files: num(b.keepFiles) })}</p>
        {b.nextDbAt || b.nextFilesAt ? (
          <p>{t("monitoring.reporter.backup.next", { db: fmtDateTime(b.nextDbAt), files: fmtDateTime(b.nextFilesAt) })}</p>
        ) : null}
      </div>
    </Section>
  );
}

function LastRun({ b, t }: { b: ReporterBackupView; t: T }) {
  const run = b.lastRun;
  return (
    <Section title={t("monitoring.reporter.backup.runTitle")}>
      {run ? (
        <p className="flex flex-wrap items-center gap-2 text-[13px]">
          <StatusPill tone={run.ok ? "ok" : "lt"}>
            {t(run.ok ? "monitoring.reporter.backup.runOk" : "monitoring.reporter.backup.runFailed")}
          </StatusPill>
          <span className="crm-tnum">{fmtDateTime(run.at)}</span>
          {run.errors > 0 ? <span className="crm-sub">{t("monitoring.reporter.backup.errors", { n: run.errors })}</span> : null}
        </p>
      ) : (
        <p className="crm-mut text-[12.5px]">{t("monitoring.reporter.backup.runNone")}</p>
      )}
    </Section>
  );
}

function Updates({ history, t }: { history: ReporterHistoryRow[]; t: T }) {
  const rows = updateRows(history);
  return (
    <Section title={t("monitoring.reporter.backup.updatesTitle")}>
      <p className="crm-sub mb-2 text-[12.5px]">{t("monitoring.reporter.backup.updatesSub")}</p>
      {rows.length === 0 ? (
        <p className="crm-mut text-[12.5px]">{t("monitoring.reporter.backup.updatesNone")}</p>
      ) : (
        <ul className="flex flex-col gap-1 text-[13px]">
          {rows.map((h) => (
            <li key={h.id} className="flex flex-wrap items-center gap-2">
              <span className="crm-mut crm-tnum w-[120px] shrink-0">{fmtDateTime(h.receivedAt)}</span>
              <StatusPill tone={verdictTone(h.backupBefore)}>
                {t(`monitoring.reporter.backup.verdict.${backupVerdict(h.backupBefore)}`)}
              </StatusPill>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function Database({ d, t }: { d: ReporterDatabaseView; t: T }) {
  const cleanup = cleanupText(t, d);
  const rows: { key: string; value: string; warn?: boolean }[] = [
    { key: "size", value: d.sizeBytes === null ? "-" : fmtBytes(d.sizeBytes) },
    { key: "tables", value: num(d.tables) },
    {
      key: "autoload",
      value:
        d.autoloadBytes === null
          ? "-"
          : d.autoloadCount === null
            ? fmtBytes(d.autoloadBytes)
            : t("monitoring.reporter.backup.autoloadValue", { size: fmtBytes(d.autoloadBytes), n: d.autoloadCount }),
      warn: autoloadHeavy(d.autoloadBytes),
    },
    ...DB_COUNTS.map((c) => ({ key: c, value: num(d[c]) })),
  ];
  return (
    <Section title={t("monitoring.reporter.backup.dbTitle")}>
      <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1 text-[13px]">
        {rows.map((r) => (
          <div key={r.key} className="contents">
            <dt className="crm-sub">{t(`monitoring.reporter.backup.db.${r.key}`)}</dt>
            <dd className={cn("crm-tnum", r.warn && "text-[var(--color-warning)]")}>{r.value}</dd>
          </div>
        ))}
      </dl>
      {cleanup ? (
        <div className="crm-sub mt-3 space-y-0.5 text-[12.5px]">
          <p>{cleanup}</p>
          {d.cleanup?.nextRunAt ? <p>{t("monitoring.reporter.backup.cleanupNext", { when: fmtDateTime(d.cleanup.nextRunAt) })}</p> : null}
        </div>
      ) : null}
    </Section>
  );
}

export function BackupTab({
  report,
  history,
  expect,
  t,
}: {
  report: ReporterReportView;
  history: ReporterHistoryRow[];
  expect: ReporterExpectView;
  t: T;
}) {
  const b = report.backup;
  const d = report.database;
  const outdated = backupState(report, expect) === "outdated";
  return (
    <div className="space-y-6 text-[13px]">
      {outdated ? (
        <p className="text-[var(--color-warning)]">{t("monitoring.reporter.backup.outdatedBody", { version: report.pluginVersion })}</p>
      ) : null}
      {b && (b.tool === "updraftplus" || b.sets.length > 0) ? (
        <>
          <Sets b={b} t={t} />
          <LastRun b={b} t={t} />
        </>
      ) : null}
      <Updates history={history} t={t} />
      {d ? (
        <Database d={d} t={t} />
      ) : (
        <Section title={t("monitoring.reporter.backup.dbTitle")}>
          <p className="crm-mut text-[12.5px]">{t("monitoring.reporter.backup.dbNone")}</p>
        </Section>
      )}
    </div>
  );
}
